import { useState } from "react";
import { Button, Flex, Image, Modal, Popover, Tag, Tooltip, Typography } from "antd";
import { EyeOutlined, InfoCircleOutlined } from "@ant-design/icons";
import {
  getPayoutAdminNote,
  isPayoutRejected,
  payoutStatusColor,
} from "../../utils/payoutDisplay";

const { Paragraph } = Typography;

type PayoutStatusCellProps = {
  status?: string;
  record: Record<string, unknown>;
};

export default function PayoutStatusCell({ status, record }: PayoutStatusCellProps) {
  const [screenshotOpen, setScreenshotOpen] = useState(false);
  const label = status || "N/A";
  const normalizedStatus = label.toLowerCase();
  const adminNote = getPayoutAdminNote(record);
  const showAdminNote = isPayoutRejected(label) && adminNote;
  const showPayoutViewAction = normalizedStatus === "approved";
  const screenshotUrl =
    typeof record.payment_screenshot_url === "string"
      ? record.payment_screenshot_url
      : "";

  return (
    <>
      <Flex align="center" gap={8} wrap="nowrap">
        <Tag
          color={
            payoutStatusColor[normalizedStatus] ||
            (label === "approved"
              ? "success"
              : label === "pending"
                ? "warning"
                : "default")
          }
          className="capitalize !m-0"
        >
          {label}
        </Tag>
        {showPayoutViewAction && (
          <Tooltip
            title={
              screenshotUrl
                ? "View payment screenshot"
                : "Payment screenshot not uploaded yet"
            }
          >
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined style={{ fontSize: 15 }} />}
              aria-label="View payment screenshot"
              disabled={!screenshotUrl}
              onClick={(event) => {
                event.stopPropagation();
                if (screenshotUrl) {
                  setScreenshotOpen(true);
                }
              }}
              style={{ width: 24, height: 24, minWidth: 24, padding: 0 }}
            />
          </Tooltip>
        )}
        {showAdminNote && (
          <Popover
            title="Admin note"
            trigger="click"
            placement="leftTop"
            content={
              <Paragraph
                className="!mb-0 !text-[13px] !text-gray-700 whitespace-pre-wrap break-words"
                style={{ maxWidth: 320 }}
              >
                {adminNote}
              </Paragraph>
            }
          >
            <Tooltip title="View admin note">
              <Button
                danger
                type="text"
                size="small"
                icon={<InfoCircleOutlined style={{ fontSize: 14 }} />}
                aria-label="View admin note"
                style={{ width: 24, height: 24, minWidth: 24, padding: 0 }}
              />
            </Tooltip>
          </Popover>
        )}
      </Flex>
      <Modal
        title="Payment Screenshot"
        open={screenshotOpen}
        onCancel={() => setScreenshotOpen(false)}
        footer={null}
        centered
        destroyOnHidden
      >
        <Image
          src={screenshotUrl}
          alt="Payment screenshot"
          style={{ width: "100%", maxHeight: 520, objectFit: "contain" }}
        />
      </Modal>
    </>
  );
}
